"use client";

import { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  Search,
  GripVertical,
  Trash2,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Checkbox } from "@/shadcn-components/ui/checkbox";
import { Alert, AlertDescription } from "@/shadcn-components/ui/alert";
import {
  BackIcon,
  SearchIcon,
  BurgerIcon,
  SelectAllIcon,
  DeleteIcon,
} from "@/components/icons";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client";
import { useQuotesWatchlistStore } from "@/lib/quotes-watchlist/store";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";

interface SymbolEditScreenProps {
  onBack: () => void;
  selectedSymbols?: string[];
  availableSymbols?: string[];
  onSymbolsChange?: (symbols: string[]) => void;
  onSymbolsReorder?: (symbols: string[]) => void;
  isDesktop?: boolean;
}

export default function SymbolEditScreen({
  onBack,
  selectedSymbols = [],
  availableSymbols = [],
  onSymbolsChange,
  onSymbolsReorder,
  isDesktop = false,
}: SymbolEditScreenProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState<string[]>([]);

  // Get subscribed symbols from store
  const allSubscribed = useSubscribedSymbolsStore(
    (state) => state.allSubscribed
  );

  // Get quotes watchlist store
  const quotesWatchlistStore = useQuotesWatchlistStore();
  const watchlist = quotesWatchlistStore.watchlist;

  // Get symbols store
  const symbolsStore = useSymbolsStore();

  // Use watchlist symbols for display
  const watchlistSymbols = watchlist.map((item) => item.symbolKey);
  const [symbols, setSymbols] = useState<string[]>(watchlistSymbols);

  const dragItemRef = useRef<number | null>(null);
  const dragOverItemRef = useRef<number | null>(null);

  // Flag to track if we've initialized the symbols list
  // This prevents the live stream from constantly resetting the user's custom order
  const isInitializedRef = useRef(false);

  // Update symbols when watchlist changes
  useEffect(() => {
    if (!isInitializedRef.current && watchlistSymbols.length > 0) {
      // First initialization - use the order from watchlist
      setSymbols(watchlistSymbols);
      isInitializedRef.current = true;
    } else if (isInitializedRef.current && watchlistSymbols.length > 0) {
      // Subsequent updates - preserve user's custom order
      setSymbols((prevSymbols) => {
        // Check for new symbols that appeared
        const newSymbols = watchlistSymbols.filter(
          (symbol) => !prevSymbols.includes(symbol)
        );

        // Filter out symbols that are no longer in watchlist
        const existingSymbols = prevSymbols.filter((symbol) =>
          watchlistSymbols.includes(symbol)
        );

        // Return updated list maintaining order
        return [...existingSymbols, ...newSymbols];
      });
    }
  }, [watchlistSymbols]);

  const handleToggleSymbol = (symbol: string, checked: boolean) => {
    const newSelected = checked
      ? [...selected, symbol]
      : selected.filter((s) => s !== symbol);

    setSelected(newSelected);
    onSymbolsChange?.(newSelected);
  };

  const handleSelectAll = () => {
    if (selected.length === symbols.length) {
      // If all are selected, deselect all
      setSelected([]);
      onSymbolsChange?.([]);
    } else {
      // Select all
      setSelected([...symbols]);
      onSymbolsChange?.([...symbols]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.length === 0 || isDeleting) return;

    setIsDeleting(true);
    setDeleteErrors([]);

    const errors: string[] = [];
    const successfullyRemoved: string[] = [];

    try {
      // Check subscribed symbols store for trade status
      const subscribedStore = useSubscribedSymbolsStore.getState();

      // Remove from watchlist for all selected symbols
      for (const symbolCurrency of selected) {
        try {
          // Get symbol object
          const symbol = symbolsStore.getSymbolByCurrency(symbolCurrency);
          if (symbol) {
            // Check if symbol has open trades before attempting removal
            const subscribedSymbol =
              subscribedStore.getSubscribedSymbol(symbolCurrency);
            if (
              subscribedSymbol &&
              (subscribedSymbol.hasOpenBuy || subscribedSymbol.hasOpenSell)
            ) {
              errors.push(`${symbolCurrency} has open trades`);
              continue;
            }

            await quotesWatchlistStore.removeFromWatchlist(symbol);
            successfullyRemoved.push(symbolCurrency);
          }
        } catch (error) {
          console.error(
            `Failed to remove ${symbolCurrency} from watchlist:`,
            error
          );
          errors.push(`${symbolCurrency} removal failed`);
        }
      }

      // Remove successfully deleted symbols from the list
      const remainingSymbols = symbols.filter(
        (s) => !successfullyRemoved.includes(s)
      );
      setSymbols(remainingSymbols);

      // Update selection to only include symbols that couldn't be removed
      const remainingSelected = selected.filter(
        (s) => !successfullyRemoved.includes(s)
      );
      setSelected(remainingSelected);

      // Notify parent about the new order
      onSymbolsReorder?.(remainingSymbols);

      // Set errors if any
      setDeleteErrors(errors);

      // Clear errors after 5 seconds
      if (errors.length > 0) {
        setTimeout(() => setDeleteErrors([]), 5000);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    dragItemRef.current = index;
    setDraggedIndex(index);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    dragOverItemRef.current = index;
    setDragOverIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    if (dragItemRef.current !== null && dragOverItemRef.current !== null) {
      const dragItem = symbols[dragItemRef.current];
      const newSymbols = [...symbols];

      // Remove the dragged item
      newSymbols.splice(dragItemRef.current, 1);

      // Insert it at the new position
      newSymbols.splice(dragOverItemRef.current, 0, dragItem);

      setSymbols(newSymbols);

      // Save the new order to quotes watchlist store
      quotesWatchlistStore.updateWatchlistOrder(newSymbols);

      // Notify about the new order
      onSymbolsReorder?.(newSymbols);
    }

    // Reset
    dragItemRef.current = null;
    dragOverItemRef.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
  };

  // Touch handling for mobile
  const handleTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    index: number
  ) => {
    const touch = e.touches[0];
    dragItemRef.current = index;
    setDraggedIndex(index);
    setIsDragging(true);

    // Create a draggable element for mobile
    const element = e.currentTarget;
    element.style.position = "fixed";
    element.style.zIndex = "1000";
    element.style.top = `${touch.clientY}px`;
    element.style.left = `${touch.clientX}px`;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const elementBelow = document.elementFromPoint(
      touch.clientX,
      touch.clientY
    );

    if (elementBelow) {
      const symbolElement = elementBelow.closest("[data-symbol-index]");
      if (symbolElement) {
        const index = parseInt(
          symbolElement.getAttribute("data-symbol-index") || "0"
        );
        if (index !== dragOverIndex) {
          setDragOverIndex(index);
          dragOverItemRef.current = index;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div
        className={`flex items-center justify-between ${
          isDesktop ? "px-3 py-2 h-10" : "px-4 py-3 h-14"
        } bg-background border-b border-border`}
      >
        <button onClick={onBack} className="p-1">
          <BackIcon size={isDesktop ? 16 : 24} className="text-blue-500" />
        </button>
        <h1
          className={`${
            isDesktop ? "text-xs" : "text-lg"
          } font-medium text-foreground`}
        >
          {selected.length > 0 ? `${selected.length} selected` : "Edit Quotes"}
        </h1>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="p-1"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <div
                  className={`${
                    isDesktop ? "w-4 h-4" : "w-6 h-6"
                  } border-2 border-red-500 border-t-transparent rounded-full animate-spin`}
                />
              ) : (
                <DeleteIcon
                  size={isDesktop ? 16 : 24}
                  className="text-red-500"
                />
              )}
            </button>
          )}
          <button
            onClick={handleSelectAll}
            className="p-1"
            disabled={isDeleting}
          >
            <SelectAllIcon
              size={isDesktop ? 16 : 24}
              className="text-foreground"
            />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div
        className={`${
          isDesktop ? "px-3 py-2" : "px-4 py-3"
        } border-b border-border`}
      >
        <div
          className={`w-full bg-accent rounded-lg ${
            isDesktop ? "py-2 px-3" : "py-3.5 px-4"
          } flex items-center gap-3`}
        >
          <SearchIcon
            size={isDesktop ? 16 : 20}
            className="text-muted-foreground"
          />
          <span
            className={`text-muted-foreground ${
              isDesktop ? "text-xs" : "text-[15px]"
            }`}
          >
            enter symbol for search
          </span>
        </div>
      </div>

      {/* Error messages */}
      {deleteErrors.length > 0 && (
        <div className="px-4 py-3">
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
            <p className="text-red-400 text-sm font-medium mb-1">
              Cannot remove {deleteErrors.length} symbol
              {deleteErrors.length > 1 ? "s" : ""}:
            </p>
            <ul className="text-red-400/80 text-xs space-y-1">
              {deleteErrors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Empty state */}
      {symbols.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p className="text-lg mb-2">No symbols in watchlist</p>
            <p className="text-sm">
              Add symbols to your watchlist from the Symbols tab first
            </p>
          </div>
        </div>
      )}

      {/* Symbols List */}
      {symbols.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          {symbols.map((symbol, index) => (
            <div
              key={symbol}
              data-symbol-index={index}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, index)}
              className={`transition-all ${
                dragOverIndex === index && draggedIndex !== index
                  ? "border-t-2 border-primary"
                  : ""
              }`}
            >
              <div
                className={`flex items-center justify-between ${
                  isDesktop ? "px-3 py-2" : "px-4 py-3"
                } transition-all border-b border-border ${
                  draggedIndex === index ? "opacity-50 scale-95" : ""
                } ${
                  isDragging && draggedIndex !== index
                    ? "transition-transform"
                    : ""
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() =>
                      handleToggleSymbol(symbol, !selected.includes(symbol))
                    }
                    className="flex items-center gap-3 flex-1"
                    disabled={isDeleting}
                  >
                    <div
                      className={`${
                        isDesktop ? "w-4 h-4" : "w-6 h-6"
                      } rounded-full border-2 ${
                        selected.includes(symbol)
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-500"
                      } flex items-center justify-center transition-colors`}
                    >
                      {selected.includes(symbol) && (
                        <svg
                          width={isDesktop ? "12" : "16"}
                          height={isDesktop ? "12" : "16"}
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span
                      className={`text-foreground ${
                        isDesktop ? "text-sm" : "text-[17px]"
                      } font-normal`}
                    >
                      {symbol}
                    </span>
                  </button>
                </div>
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="cursor-move p-2 -mr-2 touch-none hover:bg-gray-800 rounded"
                >
                  <svg
                    width={isDesktop ? "16" : "20"}
                    height={isDesktop ? "16" : "20"}
                    viewBox="0 0 20 20"
                    fill="none"
                    className="text-muted-foreground"
                  >
                    <path
                      d="M3 5h14M3 10h14M3 15h14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
